import React, { useEffect, useState } from "react";
import { Loader2, Save, Trash2, Globe, Plus } from "lucide-react";
import { toast } from "sonner";

import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  adminListTenants,
  adminUpsertTenant,
  adminDeleteTenant,
} from "@/lib/api";

export default function AdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {slug, name, domain, billing_email}

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminListTenants();
      setTenants(d.tenants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando tenants.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing) return;
    const slug = (editing.slug || "").trim().toLowerCase().replace(/[^a-z0-9-]/g, "-");
    if (!/^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/.test(slug)) {
      toast.error("Slug inválido (3-32 chars [a-z0-9-]).");
      return;
    }
    try {
      await adminUpsertTenant(slug, {
        slug,
        name: editing.name || slug,
        domain: editing.domain || "",
        billing_email: editing.billing_email || "",
      });
      toast.success(`Tenant guardado: ${slug}`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando tenant.");
    }
  };

  const del = async (slug) => {
    if (!window.confirm(`Eliminar tenant "${slug}"?`)) return;
    try {
      await adminDeleteTenant(slug);
      toast.success(`Eliminado: ${slug}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error eliminando tenant.");
    }
  };

  return (
    <div className="min-h-screen bg-white text-ink">
      <TopNav />
      <div className="mx-auto max-w-5xl px-6 py-12 sm:py-16">
        <p className="eyebrow flex items-center gap-1.5">
          <Globe className="h-3 w-3" /> Administración · Multi-tenant
        </p>
        <h1 className="mt-2 font-display text-4xl text-ink sm:text-5xl">
          Tenants y subdominios
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600">
          Registro de subdominios (<code className="font-mono text-accent-blue">{"{slug}"}.inviktus.com</code>).
          Hoy es solo informativo: el aislamiento de datos de cada cliente lo hace la empresa
          asignada a cada usuario y agente (Administración → Clientes), no el subdominio.
        </p>

        <div className="mt-8 flex items-center justify-between">
          <p className="text-[12px] text-slate-500">{tenants.length} tenants registrados</p>
          <Button onClick={() => setEditing({ slug: "", name: "", domain: "", billing_email: "" })}
                  className="rounded-sm bg-ink text-[11px] uppercase tracking-[0.2em] text-white hover:bg-ink-2"
                  data-testid="add-tenant-btn">
            <Plus className="mr-2 h-3.5 w-3.5" /> Nuevo tenant
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando…
          </div>
        ) : tenants.length === 0 ? (
          <div className="mt-6 rounded-sm border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500"
               data-testid="tenants-empty">
            No hay tenants registrados todavía.
          </div>
        ) : (
          <div className="mt-6 divide-y divide-slate-200 border border-slate-200">
            {tenants.map((t) => (
              <article key={t.slug} className="grid grid-cols-1 gap-3 p-5 lg:grid-cols-[1fr_auto] lg:items-center"
                       data-testid={`tenant-row-${t.slug}`}>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-display text-lg text-ink">{t.name || t.slug}</p>
                    <span className="rounded-sm border border-accent-blue/40 bg-accent-blue/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-accent-blue">
                      {t.slug}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[12px] text-slate-500">
                    {t.domain ? <>dominio: <span className="font-mono text-accent-blue">{t.domain}</span></> : <span className="text-slate-400">sin dominio asignado</span>}
                    {t.billing_email ? <> · facturación: {t.billing_email}</> : null}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => setEditing(t)} size="sm" variant="outline"
                          className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.16em]"
                          data-testid={`edit-tenant-${t.slug}`}>
                    Editar
                  </Button>
                  {t.slug !== "default" && (
                    <button onClick={() => del(t.slug)}
                            className="text-[11px] text-red-600 hover:underline"
                            data-testid={`delete-tenant-${t.slug}`}>
                      <Trash2 className="mr-0.5 inline h-3 w-3" /> eliminar
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/70 p-4">
            <div className="w-full max-w-lg rounded-sm bg-white shadow-2xl">
              <div className="border-b border-slate-200 p-6">
                <p className="eyebrow text-accent-blue">Tenant</p>
                <h2 className="mt-1 font-display text-2xl text-ink">
                  {editing.created_at ? "Editar tenant" : "Nuevo tenant"}
                </h2>
              </div>
              <div className="space-y-4 p-6">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Slug</label>
                  <Input value={editing.slug}
                         onChange={(e) => setEditing((p) => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") }))}
                         disabled={!!editing.created_at}
                         placeholder="acme, cynopia, globant…"
                         className="mt-1 rounded-sm border-slate-300 font-mono"
                         data-testid="tenant-slug-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Nombre visible</label>
                  <Input value={editing.name}
                         onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))}
                         placeholder="ACME Corp"
                         className="mt-1 rounded-sm border-slate-300"
                         data-testid="tenant-name-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Dominio personalizado (opcional)</label>
                  <Input value={editing.domain || ""}
                         onChange={(e) => setEditing((p) => ({ ...p, domain: e.target.value.toLowerCase() }))}
                         placeholder="abc.acme.com"
                         className="mt-1 rounded-sm border-slate-300 font-mono text-[11px]"
                         data-testid="tenant-domain-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Email de facturación</label>
                  <Input value={editing.billing_email || ""}
                         onChange={(e) => setEditing((p) => ({ ...p, billing_email: e.target.value }))}
                         placeholder="ar@acme.com"
                         className="mt-1 rounded-sm border-slate-300"
                         data-testid="tenant-billing-input" />
                </div>
              </div>
              <div className="flex justify-end gap-2 border-t border-slate-200 p-4">
                <Button variant="outline" onClick={() => setEditing(null)}
                        className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.18em]">
                  Cancelar
                </Button>
                <Button onClick={save}
                        className="rounded-sm bg-emerald-600 text-[11px] uppercase tracking-[0.18em] text-white hover:bg-emerald-700"
                        data-testid="save-tenant-btn">
                  <Save className="mr-2 h-3.5 w-3.5" /> Guardar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
